import { HubOption, HubOptionStateValue } from 'dorian-shared/types/branch/HubOption';
import type { HubSettings } from 'dorian-shared/types/branch/HubOption';
import {
  serializeHubOptionMods,
  deserializeStateConditions,
  deserializeTextDestConditions,
  deserializeIconConditions,
  deserializeTagConditions,
  deserializeReplayConditions,
  applyHubSettingsDeserializers,
  applyHubSettingsSerializers,
  applyHubOptionDeserializers,
  applyHubOptionSerializers,
} from './hubSerialize';
import { deserializeBorderConditions } from './hubOptionBorderSerialize';
import { isValidBorderHex } from './borderConditionValidation';
import { createDefaultReplayDefaults } from './hubStepDefaults';
// Side-effect imports: feature modules self-register their transforms.
import './hubProgressBarsSerialize';
import './hubOptionScoreSerialize';

/**
 * Post-load transform: convert API mods into frontend condition arrays.
 * Called once per option after data is fetched from the backend.
 */
export function deserializeHubOptionFromApi(opt: HubOption): void {
  if (opt.mods && opt.mods.length > 0) {
    opt.stateConditions = deserializeStateConditions(opt.mods); // eslint-disable-line no-param-reassign
    opt.textDestConditions = deserializeTextDestConditions(opt.mods); // eslint-disable-line no-param-reassign
    opt.iconConditions = deserializeIconConditions(opt.mods); // eslint-disable-line no-param-reassign
    opt.replayConditions = deserializeReplayConditions(opt.mods); // eslint-disable-line no-param-reassign
    opt.tagTopConditions = deserializeTagConditions(opt.mods, 'top'); // eslint-disable-line no-param-reassign
    opt.tagBottomConditions = deserializeTagConditions(opt.mods, 'bottom'); // eslint-disable-line no-param-reassign
    opt.borderConditions = deserializeBorderConditions(opt.mods); // eslint-disable-line no-param-reassign
  }
  applyHubOptionDeserializers(opt);
}

/**
 * Pre-save transform: convert frontend condition arrays back to API mods
 * and clean up transient fields before PUT payload.
 * Called once per option before data is sent to the backend.
 */
export function prepareHubOptionForSave(opt: HubOption): void {
  // Sanitize invalid borderColor → null before serialization.
  // Any value that fails isValidBorderHex (e.g. "#HUY", "FF5733", "#FF") is dropped
  // so payload never carries garbage. Empty/missing stays null.
  if (opt.borderColor != null && !isValidBorderHex(opt.borderColor)) {
    opt.borderColor = null; // eslint-disable-line no-param-reassign
  }
  // Drop invalid-color borderConditions entirely — a condition with garbage color is meaningless.
  if (opt.borderConditions && opt.borderConditions.length > 0) {
    opt.borderConditions = opt.borderConditions.filter( // eslint-disable-line no-param-reassign
      (c) => isValidBorderHex(c.color),
    );
  }

  // Serialize frontend-only conditions to mods array
  opt.mods = serializeHubOptionMods(opt); // eslint-disable-line no-param-reassign
  delete opt.stateConditions; // eslint-disable-line no-param-reassign
  delete opt.replayConditions; // eslint-disable-line no-param-reassign
  delete opt.textDestConditions; // eslint-disable-line no-param-reassign
  delete opt.iconConditions; // eslint-disable-line no-param-reassign
  delete opt.tagTopConditions; // eslint-disable-line no-param-reassign
  delete opt.tagBottomConditions; // eslint-disable-line no-param-reassign
  delete opt.borderConditions; // eslint-disable-line no-param-reassign

  // Ensure required arrays exist (global JSON replacer strips empty arrays)
  if (!opt.mods) opt.mods = []; // eslint-disable-line no-param-reassign
  if (!opt.tags) opt.tags = { top: [], bottom: [] }; // eslint-disable-line no-param-reassign

  const hasReplayMods = (opt.mods as { prop?: string }[]).some((m) => m.prop?.startsWith('replay.'));
  if (opt.replay == null && hasReplayMods) {
    opt.replay = createDefaultReplayDefaults(); // eslint-disable-line no-param-reassign
  } else if (opt.replay && !hasReplayMods
    && opt.replay.state === HubOptionStateValue.Hidden
    && opt.replay.gotoBranchId == null
    && opt.replay.iconId == null) {
    opt.replay = null; // eslint-disable-line no-param-reassign
  }

  applyHubOptionSerializers(opt);
}

// Step-level HubSettings transforms. Features extend by registering, not by editing here.
export function deserializeHubSettingsFromApi(settings?: HubSettings | null): void {
  if (!settings) return;
  applyHubSettingsDeserializers(settings);
}

export function prepareHubSettingsForSave(settings?: HubSettings | null): void {
  if (!settings) return;
  applyHubSettingsSerializers(settings);
}
